// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { useEffect, useState, useCallback } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api, type FeeTiersResponse, type WalletUtxo, type WalletTx } from '../api.js';
import { useSession, type AgentKind } from '../session-context.js';
import type { NetworkName, WalletSpendEstimate } from '../key-agent-loader.js';
import { formatBtc, formatSats, parseBtcToSats } from '../format.js';
import { balancePanelModel, type BalancePanelModel } from '../balance.js';
import { TransactionList } from '../components/transaction-list.js';
import { BalancePanel } from '../components/balance-panel.js';
import { DepositAddressField } from '../components/deposit-address-field.js';
import { PasswordManagerUsername } from '../components/password-manager-username.js';
import { WipeBrowserCard } from '../components/wipe-browser-card.js';
import {
  FeeSelector,
  feeSelectionError,
  initialFeeSelection,
  rateMovedMaterially,
  rateMovedNotice,
  selectedFeeRate,
  validateCustomFeeRate,
  type FeeOptionFees,
  type FeeSelection,
} from '../components/fee-selector.js';
import '../styles/accept.css';
import '../styles/wallet.css';
import '../styles/vault-detail.css';
// COB-485 Ticket B: the FeeSelector renders `shared-vault-notice` /
// `sv-allowlist-warn` / `shared-vault-fee-label` (shared-vaults.css) and
// `vault-card-error` (home.css). Pulling both sheets in is the established
// pattern for a borrowed component — shared-vault-detail.tsx imports
// wallet.css for exactly the same reason (TransactionList's classes).
import '../styles/shared-vaults.css';
import '../styles/home.css';

export function Wallet() {
  const session = useSession();
  const { route } = useLocation();

  // Logged-in users only — bounce to home otherwise.
  //
  // COB-504: and that is the ONLY gate on this page. Pre-KYC access is
  // deliberate — KYC gates **strategy vaults** and nothing else. The wallet
  // is the user's own P2WPKH address derived from their own key: no Cobault
  // contract, no counterparty, nothing for a verification check to be a
  // precondition of. The backend agrees and is the authority: the sole
  // server-side KYC gate is strategy `create_vault` (lib.rs, F3); no
  // `/wallet/*` route has one, so the F17.3 redirect that used to live here
  // was blocking a page the server would have served. It also stranded
  // pre-KYC users from the COB-500 "Flush browser stored key material"
  // control, which lives at the foot of this page.
  //
  // The layout's KYC banner still shows (it is a nudge, not a gate), and
  // `pages/accept.tsx` keeps its redirect — that one mirrors a real
  // server-side refusal.
  useEffect(() => {
    if (!session.isLoggedIn) route('/');
  }, [session.isLoggedIn, route]);

  // Wallet address derivation + registration on mount.
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);

  useEffect(() => {
    if (!session.agent || !session.isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const addr = await session.agent!.getWalletAddress();
        if (cancelled) return;
        // Register before exposing the address to the cards. The cards
        // call /wallet/utxos and /wallet/txs on mount; if they fire
        // before registration lands, the backend 400s and the user sees
        // a flash of error. Order matters.
        await api.registerWallet({ wallet_address: addr });
        if (cancelled) return;
        setWalletAddress(addr);
      } catch (e) {
        if (cancelled) return;
        setRegisterError(
          e instanceof Error ? e.message : 'Failed to register wallet address.',
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session.agent, session.isAuthenticated]);

  if (!session.isLoggedIn) return null;

  const faucetEnabled =
    session.bitcoinNetwork !== null && session.bitcoinNetwork !== 'bitcoin';

  return (
    <div class="wallet-page">
      <h1 class="wallet-title">Wallet</h1>
      <p class="wallet-subtitle">
        Manage funds at your personal wallet address. Separate from your
        vault keys —{' '}
        {session.agentKind === 'hardware'
          ? 'its own account on your hardware device.'
          : 'same seed, different derivation path.'}
      </p>

      {registerError && <p class="wallet-error">{registerError}</p>}

      {walletAddress && <AddressCard address={walletAddress} />}
      {walletAddress && <SendCard walletAddress={walletAddress} />}
      {walletAddress && <TransactionsCard />}
      {walletAddress && backupCardVisible(session.agentKind) && <ExportSeedCard />}
      {faucetEnabled && walletAddress && <FaucetPanel walletAddress={walletAddress} />}
      {/* COB-500: last card on the page, and NOT gated on `walletAddress` —
          a session whose wallet address failed to derive or register is
          exactly one that may want to flush it. COB-506 narrowed it to
          software custody (`wipeCardVisible`); the flow itself is mounted in
          `Layout` because this page unmounts the moment the wipe logs the
          session out. */}
      {wipeCardVisible(session.agentKind) && <WipeBrowserCard />}
    </div>
  );
}

/** The backup-phrase card is only meaningful under the browser key-agent,
 *  where the 24-word seed is the user's own recoverable secret. Delegated
 *  (web3auth) custody and hardware sessions have no browser-held seed to
 *  reveal — offering a "recover seed" affordance there would be a false,
 *  server-side-style promise we must never make. Exported for tests
 *  (kycBlocksAccess precedent). */
export function backupCardVisible(agentKind: AgentKind | null): boolean {
  return agentKind === 'software';
}

/** COB-506: "Flush browser stored key material" is offered to software
 *  custody only. It shipped (COB-500) for every backend, and the flow behind
 *  it still handles all three — the key-agent `wipeAccount` RPC, the
 *  delegated `executeBrowserWipe` path and the modal's non-software arms are
 *  all kept, tested and re-exposable. What changed is reachability: a browser
 *  seed is the only key material a browser actually stores, so this is the
 *  only custody model where the control names something the user has here.
 *  Deliberately a predicate of its own rather than a second caller of
 *  `backupCardVisible` — the two cards agree today by coincidence of the same
 *  reasoning, not because one gate governs both. */
export function wipeCardVisible(agentKind: AgentKind | null): boolean {
  return agentKind === 'software';
}

// ── Address + balance ────────────────────────────────────────────────

/** COB-355: the wallet balance panel leads with the spendable (confirmed)
 *  figure — the Send flow spends confirmed UTXOs only — and surfaces the
 *  still-confirming remainder as a muted "+X BTC awaiting confirmation"
 *  note, mirroring the shared-vault balance (the estate-wide spendable-first
 *  rule). Pure; exported for tests. A `null` utxo set (not yet loaded)
 *  yields a null headline so the panel renders the em-dash placeholder. */
export function walletBalancePanel(utxos: WalletUtxo[] | null): BalancePanelModel {
  if (utxos === null) return { headlineSats: null, pendingSats: null };
  const total = utxos.reduce((acc, u) => acc + u.value, 0);
  const confirmed = utxos
    .filter((u) => u.status.confirmed)
    .reduce((acc, u) => acc + u.value, 0);
  return balancePanelModel({ balance_sats: total, confirmed_balance_sats: confirmed });
}

function AddressCard({ address }: { address: string }) {
  const [utxos, setUtxos] = useState<WalletUtxo[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const u = await api.getWalletUtxos();
      setUtxos(u);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load UTXOs.');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const { headlineSats, pendingSats } = walletBalancePanel(utxos);

  return (
    <div class="wallet-section">
      <div class="wallet-section-title">Your wallet address</div>
      <DepositAddressField address={address} label="" />
      <BalancePanel label="Balance" headlineSats={headlineSats} pendingSats={pendingSats} />
      {error && <p class="wallet-error">{error}</p>}
      <button class="btn btn-link" onClick={refresh}>
        Refresh
      </button>
    </div>
  );
}

// ── Faucet panel (F6, kept) ──────────────────────────────────────────

function FaucetPanel({ walletAddress }: { walletAddress: string }) {
  const session = useSession();
  const [expanded, setExpanded] = useState(false);
  const [address, setAddress] = useState(walletAddress);
  const [btcInput, setBtcInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ txid: string; amount_sats: number } | null>(null);

  const amountSats = parseBtcToSats(btcInput);
  const validAmount = isFinite(amountSats) && amountSats > 0;
  const validAddress = address.trim().length > 0;

  async function handleSubmit() {
    setError(null);
    setSuccess(null);
    if (!validAddress) {
      setError('Enter a destination address.');
      return;
    }
    if (!validAmount) {
      setError('Enter a valid BTC amount greater than zero.');
      return;
    }
    setLoading(true);
    try {
      const resp = await api.requestFaucet({
        address: address.trim(),
        amount_sats: amountSats,
      });
      setSuccess({ txid: resp.txid, amount_sats: resp.amount_sats });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Faucet request failed.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div class="wallet-section">
      <button
        type="button"
        class="button-reset wallet-section-title"
        onClick={() => setExpanded(!expanded)}
      >
        Testing only {expanded ? '▾' : '▸'}
      </button>
      {expanded && (
        <>
          <p class="wallet-section-desc">
            Get test sBTC on <strong>{session.bitcoinNetwork}</strong>. Send to any
            address — pre-filled with your wallet, but you can paste somewhere else.
          </p>
          <div class="accept-field">
            <label class="accept-label">Destination address</label>
            <input
              class="accept-input mono"
              type="text"
              value={address}
              onInput={(e) => setAddress((e.target as HTMLInputElement).value)}
              autocomplete="off"
            />
          </div>
          <div class="accept-field">
            <label class="accept-label">Amount (BTC)</label>
            <input
              class="accept-input tabular-nums"
              type="text"
              inputMode="decimal"
              placeholder="0.01"
              value={btcInput}
              onInput={(e) => setBtcInput((e.target as HTMLInputElement).value)}
            />
            {validAmount && (
              <p class="accept-input-hint tabular-nums">{formatBtc(amountSats)} BTC</p>
            )}
          </div>
          {error && <p class="wallet-error">{error}</p>}
          {success && (
            <div class="wallet-success">
              <div class="wallet-success-label">
                Sent {formatBtc(success.amount_sats)} BTC
              </div>
              <span class="mono">{success.txid}</span>
            </div>
          )}
          <button
            class="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || !validAddress || !validAmount}
          >
            {loading ? 'Sending...' : 'Send test sBTC'}
          </button>
        </>
      )}
    </div>
  );
}

// ── Send transaction ─────────────────────────────────────────────────

/** COB-485 Ticket B: settle-time before re-pricing after a keystroke, so
 *  typing an address or an amount does not fan out one estimate per
 *  character. Same window the shared-vault send form uses. */
const FEE_ESTIMATE_DEBOUNCE_MS = 400;

/** COB-485 Ticket B: how often the open send form re-fetches the ladder.
 *  Twice the server's 30 s estimate cache, so a poll costs at most one
 *  upstream call per user. Matches the shared-vault form. */
const FEE_LADDER_REFRESH_MS = 60_000;

/**
 * COB-485 Ticket B: the "your change was too small to keep" line, or `null`
 * when no change was absorbed.
 *
 * This is the case the pre-send estimate exists for. Sub-dust change is not
 * worth a 31-vbyte output, so the builder drops the output and the leftover
 * goes to the miner — which means the fee actually paid can be far above
 * `rate × vsize`, and the old flow only ever revealed that *after* the
 * broadcast. Exported for tests.
 */
export function dustChangeNotice(est: WalletSpendEstimate): string | null {
  if (!est.dustChangeAbsorbed) return null;
  const absorbed = est.feeSats - est.nominalFeeSats;
  if (absorbed <= 0n) return null;
  return `This includes ${formatSats(
    Number(absorbed),
  )} sats of change too small to send back to your wallet — it goes to the miner. Send slightly less to keep it.`;
}

function sanitizeAddress(raw: string): string {
  return raw.replace(/[\s\-\u00AD\u200B]/g, '');
}

const BECH32_PREFIXES: Record<string, string> = {
  bitcoin: 'bc1',
  signet: 'tb1',
  testnet: 'tb1',
  regtest: 'bcrt1',
};

function networkMismatchHint(address: string, network: string | null): string | null {
  if (!network) return null;
  const expected = BECH32_PREFIXES[network];
  if (!expected) return null;
  const lower = address.toLowerCase();
  if (lower.startsWith(expected)) return null;
  for (const [net, prefix] of Object.entries(BECH32_PREFIXES)) {
    if (net !== network && lower.startsWith(prefix)) {
      return `This looks like a ${net} address (${prefix}…) but you're on ${network} (${expected}…).`;
    }
  }
  return null;
}

function SendCard({ walletAddress }: { walletAddress: string }) {
  const session = useSession();
  const [recipient, setRecipient] = useState('');
  const [btcInput, setBtcInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ txid: string; feeSats: bigint } | null>(null);

  // COB-485 Ticket B: the fee control. Before this, `handleSend` silently
  // picked `fees['1'] ?? fees['2'] ?? fees['6'] ?? 5` out of a raw esplora
  // map and the user learned what they had paid from the success line. Same
  // served ladder and same `<FeeSelector>` the shared-vault form uses.
  const [feeLadder, setFeeLadder] = useState<FeeTiersResponse | null>(null);
  const [feeSelection, setFeeSelection] = useState<FeeSelection>(initialFeeSelection());
  // The UTXO set the estimate is priced against. Held here rather than read
  // per-keystroke: it is the same set for minutes at a time, so it rides the
  // ladder's refresh cadence and the estimate itself stays a local call.
  const [utxos, setUtxos] = useState<WalletUtxo[] | null>(null);
  const [estimate, setEstimate] = useState<WalletSpendEstimate | null>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  // COB-485 Ticket D: what each dropdown row costs, priced by the WALLET's own
  // builder — one `estimateWalletSpend` per served rate, never the shared-vault
  // quote formula. `null` until there is a draft worth pricing.
  const [tierFees, setTierFees] = useState<FeeOptionFees | null>(null);

  const amountSats = parseBtcToSats(btcInput);
  const validAmount = isFinite(amountSats) && amountSats > 0;
  const cleanRecipient = sanitizeAddress(recipient);
  const validRecipient = cleanRecipient.length > 0;
  const mismatchHint = validRecipient ? networkMismatchHint(cleanRecipient, session.bitcoinNetwork) : null;

  const feeRate = selectedFeeRate(feeSelection, feeLadder);
  const feeError = feeSelectionError(feeSelection, feeLadder, 'wallet');
  // A rate the selector *blocks* must not be priced — quoting a total for a
  // sub-floor rate reads as endorsement of it. An un-acknowledged sub-minimum
  // rate is a different case: it is a legal rate awaiting a tick, and seeing
  // what it costs is exactly what informs the tick.
  const feeRateBlocked =
    feeSelection.mode === 'custom' &&
    validateCustomFeeRate(feeSelection.custom, feeLadder, 'wallet').kind === 'error';

  const refreshFeeLadder = useCallback(async (): Promise<FeeTiersResponse | null> => {
    if (!session.isAuthenticated) return null;
    try {
      const tiers = await api.getFeeTiers();
      setFeeLadder(tiers);
      return tiers;
    } catch {
      // Keep whatever we last had. With nothing at all the selector renders
      // its own "fetching rates" state and `feeError` holds the button.
      return null;
    }
  }, [session.isAuthenticated]);

  const refreshUtxos = useCallback(async () => {
    if (!session.isAuthenticated) return;
    try {
      setUtxos(await api.getWalletUtxos());
    } catch {
      // The estimate stays unavailable; `handleSend` re-fetches and reports
      // for real. A failed background poll is not worth an error banner.
    }
  }, [session.isAuthenticated]);

  // Fetch on mount, then keep both fresh while the form is open: every 60 s
  // and on window focus. A user who tabbed away for an hour must not send at
  // an hour-old rate. The pre-submit re-price in `handleSend` is the last line.
  useEffect(() => {
    void refreshFeeLadder();
    void refreshUtxos();
    const onFocus = () => {
      void refreshFeeLadder();
      void refreshUtxos();
    };
    const interval = setInterval(onFocus, FEE_LADDER_REFRESH_MS);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshFeeLadder, refreshUtxos]);

  // The pre-send estimate. Computed by the key agent from the same
  // `planWalletSpend` the signed transaction is built from, so the number
  // here is the fee that will be paid — dust-fold included — not a second
  // implementation of the formula that happens to agree (COB-479).
  useEffect(() => {
    const agent = session.agent;
    if (
      !agent ||
      utxos === null ||
      feeRate === null ||
      feeRateBlocked ||
      !validAmount ||
      !validRecipient ||
      mismatchHint
    ) {
      setEstimate(null);
      setEstimateError(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const est = await agent.estimateWalletSpend({
            utxos: utxos.map((u) => ({
              txid: u.txid,
              vout: u.vout,
              value: u.value,
              status: { confirmed: u.status.confirmed },
            })),
            recipient: cleanRecipient,
            amountSats: BigInt(amountSats),
            feeRateSatVb: feeRate,
          });
          if (cancelled) return;
          setEstimate(est);
          setEstimateError(null);
        } catch (e) {
          if (cancelled) return;
          // "insufficient funds: have X, need Y" and "Invalid address" are
          // worth surfacing here — before the click, while the user can still
          // change the amount — rather than as a failed send.
          setEstimate(null);
          setEstimateError(
            e instanceof Error ? e.message : 'Could not estimate the network fee.',
          );
        }
      })();
    }, FEE_ESTIMATE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    session.agent,
    utxos,
    feeRate,
    feeRateBlocked,
    amountSats,
    cleanRecipient,
    validAmount,
    validRecipient,
    mismatchHint,
  ]);

  // COB-485 Ticket D: the same estimate, once per served rate, so every row in
  // the dropdown carries what it would actually cost.
  //
  // Ticket B put no totals on the wallet's tiers because the only client-side
  // way to price them was the shared-vault quote formula — a second fee model
  // on a surface that has its own. That reasoning stands; what changed is that
  // the wallet's own builder is now reachable per-rate. `estimateWalletSpend`
  // reads no seed, needs no unlocked session, and makes no device call on the
  // Ledger backend, so pricing the whole ladder is a handful of local calls.
  //
  // Deliberately keyed on the RATES, not on the ladder object: the 60 s poll
  // hands back a fresh object every minute whether or not a number moved.
  const ladderRatesKey = feeLadder
    ? feeLadder.tiers.map((t) => `${t.key}:${t.sat_vb}`).join(',')
    : '';
  useEffect(() => {
    const agent = session.agent;
    if (
      !agent ||
      !feeLadder ||
      utxos === null ||
      !validAmount ||
      !validRecipient ||
      mismatchHint
    ) {
      // No valid draft means no honest number for any row; the dropdown falls
      // back to rates alone rather than showing a stale set of totals.
      setTierFees(null);
      return;
    }
    const tiers = feeLadder.tiers;
    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        const inputs = utxos.map((u) => ({
          txid: u.txid,
          vout: u.vout,
          value: u.value,
          status: { confirmed: u.status.confirmed },
        }));
        // The served ladder is clamped by the relay floor and forced
        // monotonic, so two tiers routinely carry the same sat/vB. Price each
        // distinct rate once and fan the answer back out.
        const rates = [...new Set(tiers.map((t) => t.sat_vb))];
        const priced = new Map<number, number>();
        await Promise.all(
          rates.map(async (rate) => {
            try {
              const est = await agent.estimateWalletSpend({
                utxos: inputs,
                recipient: cleanRecipient,
                amountSats: BigInt(amountSats),
                feeRateSatVb: rate,
              });
              priced.set(rate, Number(est.feeSats));
            } catch {
              // A rate this draft cannot afford simply has no total. Leaving
              // the row bare is honest; the selected rate's own failure is
              // still reported by `estimateError` above.
            }
          }),
        );
        if (cancelled) return;
        const next: FeeOptionFees = {};
        for (const t of tiers) {
          const fee = priced.get(t.sat_vb);
          if (fee !== undefined) next[t.key] = fee;
        }
        setTierFees(next);
      })();
    }, FEE_ESTIMATE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    session.agent,
    ladderRatesKey,
    utxos,
    amountSats,
    cleanRecipient,
    validAmount,
    validRecipient,
    mismatchHint,
  ]);

  // What the dropdown prices its rows with. The tier totals come from the loop
  // above; the `Custom` row reuses the selected-rate estimate, which is the
  // same call for the same rate.
  const feeOptionFees: FeeOptionFees = {
    ...(tierFees ?? {}),
    ...(feeSelection.mode === 'custom' && estimate
      ? { custom: Number(estimate.feeSats) }
      : {}),
  };

  async function handleSend() {
    setError(null);
    setResult(null);
    if (!validRecipient) {
      setError('Enter a destination address.');
      return;
    }
    if (mismatchHint) {
      setError(mismatchHint);
      return;
    }
    if (!validAmount) {
      setError('Enter a valid BTC amount greater than zero.');
      return;
    }
    if (feeError) {
      setError(feeError);
      return;
    }
    if (!session.agent) {
      setError('Session expired. Unlock again.');
      return;
    }
    setLoading(true);
    try {
      const freshUtxos = await api.getWalletUtxos();
      setUtxos(freshUtxos);
      if (freshUtxos.length === 0) {
        throw new Error('Wallet is empty.');
      }

      // COB-485 §2.4: re-price immediately before building. The form may have
      // sat open for a while, so the number on the control can be stale. If the
      // chosen tier has moved more than 20 %, show the new figure and require
      // one explicit re-confirm rather than signing at a rate the user never
      // saw. A custom rate is unaffected — it is not derived from the ladder.
      const rateAtRender = selectedFeeRate(feeSelection, feeLadder);
      const fresh = await refreshFeeLadder();
      const rate = selectedFeeRate(feeSelection, fresh ?? feeLadder);
      if (rate === null) {
        throw new Error('Could not read the current network fee rates. Try again.');
      }
      if (rateAtRender !== null && rateMovedMaterially(rateAtRender, rate)) {
        // `refreshFeeLadder` has already stored the new ladder, so the control
        // now shows the new number and a second click compares it against
        // itself and proceeds.
        setError(rateMovedNotice(rateAtRender, rate));
        return;
      }

      const spend = await session.agent.buildAndSignWalletSpend({
        utxos: freshUtxos.map((u) => ({
          txid: u.txid,
          vout: u.vout,
          value: u.value,
          status: { confirmed: u.status.confirmed },
        })),
        recipient: cleanRecipient,
        amountSats: BigInt(amountSats),
        feeRateSatVb: rate,
      });

      const broadcast = await api.broadcastWalletTx({ tx_hex: spend.txHex });
      setResult({ txid: broadcast.txid, feeSats: spend.feeSats });
      setBtcInput('');
      setRecipient('');
      // Back to the served default tier, not to whatever was last chosen.
      setFeeSelection(initialFeeSelection());
      void refreshUtxos();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Send failed.';
      if (msg.includes('Unknown letter') || msg.includes('Invalid address')) {
        setError('Invalid address. Check that it is a valid Bitcoin address for the current network.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  const sameAsWallet =
    cleanRecipient.length > 0 && cleanRecipient === walletAddress;
  const dustNotice = estimate ? dustChangeNotice(estimate) : null;

  return (
    <div class="wallet-section">
      <div class="wallet-section-title">Send</div>
      <p class="wallet-section-desc">
        Sweep funds out of this wallet to any address. Only confirmed UTXOs
        are spent.
        {/* HW.3: the device backend signs via the default wpkh policy, so
            the Ledger shows destination + amount before approving. */}
        {session.agentKind === 'hardware' &&
          ' You will confirm the destination and amount on your device.'}
      </p>
      <div class="accept-field">
        <label class="accept-label">Destination address</label>
        <input
          class="accept-input mono"
          type="text"
          placeholder={`${BECH32_PREFIXES[session.bitcoinNetwork ?? 'regtest'] ?? 'bc1'}q...`}
          value={recipient}
          onInput={(e) => setRecipient((e.target as HTMLInputElement).value)}
          autocomplete="off"
        />
        {mismatchHint && <p class="wallet-error">{mismatchHint}</p>}
        {!mismatchHint && sameAsWallet && (
          <p class="wallet-error">That's your own wallet address.</p>
        )}
      </div>
      <div class="accept-field">
        <label class="accept-label">Amount (BTC)</label>
        <input
          class="accept-input tabular-nums"
          type="text"
          inputMode="decimal"
          placeholder="0.001"
          value={btcInput}
          onInput={(e) => setBtcInput((e.target as HTMLInputElement).value)}
        />
        {validAmount && (
          <p class="accept-input-hint tabular-nums">{formatBtc(amountSats)} BTC</p>
        )}
      </div>
      {/* COB-485 Ticket D: every option in the dropdown carries its own total,
          priced by `estimateWalletSpend` — the wallet's own builder, never the
          shared-vault quote formula. The selected option's total therefore sits
          on the collapsed trigger, which is why the standalone "Estimated
          network fee" line Ticket B added is gone: it was the same number
          twice. The dust notice below is a different fact and stays. */}
      <FeeSelector
        ladder={feeLadder}
        selection={feeSelection}
        onChange={setFeeSelection}
        disabled={loading}
        surface="wallet"
        optionFees={feeOptionFees}
      />
      {dustNotice && <p class="accept-input-hint sv-allowlist-warn">{dustNotice}</p>}
      {estimateError && <p class="wallet-error">{estimateError}</p>}
      {error && <p class="wallet-error">{error}</p>}
      {result && (
        <div class="wallet-success">
          <div class="wallet-success-label">
            Sent · fee {formatBtc(Number(result.feeSats))} BTC
          </div>
          <span class="mono">{result.txid}</span>
        </div>
      )}
      <button
        class="btn btn-primary"
        onClick={handleSend}
        disabled={
          loading ||
          !validRecipient ||
          !validAmount ||
          sameAsWallet ||
          !!mismatchHint ||
          !!feeError
        }
      >
        {loading ? 'Sending...' : 'Send'}
      </button>
    </div>
  );
}

// ── Transaction history ──────────────────────────────────────────────

function TransactionsCard() {
  const [txs, setTxs] = useState<WalletTx[] | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function load() {
    setError(null);
    api
      .getWalletTxs()
      .then(setTxs)
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'Failed to load transactions.'),
      );
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <div class="wallet-section">
      <button
        type="button"
        class="button-reset wallet-section-title"
        onClick={() => setExpanded(!expanded)}
      >
        Transactions {expanded ? '▾' : '▸'}
      </button>
      {expanded && (
        <>
          {error && <p class="wallet-error">{error}</p>}
          {txs === null && !error && (
            <p class="wallet-section-desc">Loading...</p>
          )}
          {txs !== null && txs.length === 0 && (
            <p class="wallet-section-desc">No transactions yet.</p>
          )}
          {txs !== null && txs.length > 0 && <TransactionList txs={txs} />}
          <button class="btn btn-link" onClick={load}>
            Refresh
          </button>
        </>
      )}
    </div>
  );
}

// ── Export seed ──────────────────────────────────────────────────────
//
// Revealing the 24-word seed is the highest-impact action in the whole
// SPA — anyone with a live session could otherwise drain every key on
// the device. F14 click-through #2: gate reveal behind a passcode
// re-entry + server-side verifier check, so a left-open session alone
// is not enough. Pattern is `passcodeCheck()` from the onboarding flow;
// the SoftwareKeyAgent's own unlock state is not sufficient proof
// because it stays unlocked for the whole session.

/** How long a revealed seed stays on-screen before the card scrubs
 *  itself. 60s is a compromise between "operator reads 24 words off a
 *  piece of paper" (typical ~45s) and "walk away from the screen".
 *  Exported for tests. */
export const SEED_REVEAL_TIMEOUT_MS = 60_000;

/** Translate a PasscodeCheckStatus + any thrown error into user-facing
 *  copy for the reveal challenge. Exported so tests don't need to
 *  mount the component to cover the branches. */
export function revealFailureMessage(
  status: 'match' | 'mismatch' | 'no_user' | 'unknown',
  err?: unknown,
): string {
  if (status === 'mismatch') return 'Passcode did not match. Try again.';
  if (status === 'no_user') return 'This email is not registered.';
  if (status === 'unknown') {
    return err instanceof Error ? err.message : 'Could not verify passcode.';
  }
  return '';
}

/** Exported for the COB-502 form-semantics render tests — mounting the whole
 *  `Wallet` page would drag in the balance/UTXO/fee-tier fetches this card has
 *  nothing to do with. */
export function ExportSeedCard() {
  const session = useSession();
  // 'idle'      → button visible, no prompt
  // 'prompt'    → passcode input showing
  // 'verifying' → passcodeCheck in flight
  // 'revealed'  → seed words on-screen
  const [mode, setMode] = useState<'idle' | 'prompt' | 'verifying' | 'revealed'>('idle');
  const [passcode, setPasscode] = useState('');
  const [seed, setSeed] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Auto-hide the revealed seed after a timeout. Also scrub it if the
  // agent locks out from underneath us (e.g. idle timeout fires while
  // the seed is on screen).
  useEffect(() => {
    if (mode !== 'revealed') return;
    const t = setTimeout(() => {
      setSeed(null);
      setMode('idle');
    }, SEED_REVEAL_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [mode]);
  useEffect(() => {
    if (!session.isAuthenticated && mode === 'revealed') {
      setSeed(null);
      setMode('idle');
    }
  }, [session.isAuthenticated, mode]);

  function startChallenge() {
    setError(null);
    setPasscode('');
    setMode('prompt');
  }

  function cancel() {
    setError(null);
    setPasscode('');
    setMode('idle');
  }

  async function verifyAndReveal(e?: Event) {
    if (e) e.preventDefault();
    setError(null);
    if (!session.agent) {
      setError('Session expired. Unlock again.');
      return;
    }
    if (!session.userEmail) {
      // Shouldn't happen under normal flow — userEmail is set the moment
      // the agent unlocks. Surface a clear error rather than a silent
      // failure if something has gone sideways.
      setError('Session email unavailable. Please log out and back in.');
      return;
    }
    if (passcode.length === 0) {
      setError('Enter your passcode.');
      return;
    }
    setMode('verifying');
    try {
      const status = await session.passcodeCheck(session.userEmail, passcode);
      if (status !== 'match') {
        setError(revealFailureMessage(status as 'mismatch' | 'no_user'));
        setMode('prompt');
        setPasscode('');
        return;
      }
      const mnemonic = await session.agent.exportSeed();
      setSeed(mnemonic.split(' '));
      setPasscode('');
      setMode('revealed');
    } catch (err) {
      setError(revealFailureMessage('unknown', err));
      setMode('prompt');
      setPasscode('');
    }
  }

  function hide() {
    setSeed(null);
    setMode('idle');
  }

  return (
    <div class="wallet-section">
      <div class="wallet-section-title">Backup phrase</div>
      <p class="wallet-section-desc">
        Your 24-word seed phrase controls every key in this wallet, including
        your vault keys. Anyone who sees it can drain your funds. Never share
        it. Cobault staff will never ask for it.
      </p>
      {error && <p class="wallet-error">{error}</p>}

      {mode === 'idle' && (
        <button class="btn btn-primary" onClick={startChallenge}>
          Show seed phrase
        </button>
      )}

      {(mode === 'prompt' || mode === 'verifying') && (
        <form class="seed-challenge" onSubmit={verifyAndReveal}>
          <p class="wallet-section-desc">
            Re-enter your passcode to reveal the phrase. The phrase will hide
            itself after a minute.
          </p>
          {/* COB-502: this form already submitted, so Safari treated it as a
              credential save — with no username field, into a blank keychain
              entry for the origin. The companion attributes it to the account. */}
          {session.userEmail && (
            <PasswordManagerUsername
              id="cobault-export-username"
              email={session.userEmail}
            />
          )}
          <div class="accept-field">
            <label class="accept-label" for="cobault-export-passcode">
              Passcode
            </label>
            <input
              id="cobault-export-passcode"
              name="passcode"
              class="accept-input"
              type="password"
              autocomplete="current-password"
              autoFocus
              value={passcode}
              disabled={mode === 'verifying'}
              onInput={(e) => setPasscode((e.target as HTMLInputElement).value)}
              onChange={(e) => setPasscode((e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="seed-challenge-actions">
            <button
              type="submit"
              class="btn btn-primary"
              disabled={mode === 'verifying' || passcode.length === 0}
            >
              {mode === 'verifying' ? 'Verifying...' : 'Reveal'}
            </button>
            <button
              type="button"
              class="btn btn-secondary"
              onClick={cancel}
              disabled={mode === 'verifying'}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {mode === 'revealed' && seed && (
        <div>
          <div class="seed-warning">
            Make sure no one is watching your screen. This will hide itself
            automatically.
          </div>
          <div class="seed-grid">
            {seed.map((word, i) => (
              <div key={i} class="seed-word">
                <span class="seed-word-num">{i + 1}</span>
                <span class="seed-word-text">{word}</span>
              </div>
            ))}
          </div>
          <button class="btn btn-secondary" onClick={hide}>
            Hide
          </button>
        </div>
      )}
    </div>
  );
}

// Re-export the network name for type-safety in case future imports need it.
export type { NetworkName };
